import { computed, onMounted, onServerPrefetch, ref, watch } from 'vue';

import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

// Will be set to true after first navigation to ensure we stop using the persisted ssrTodaysDate
const didUpdateServerDate = ref(false);

function useSsrDateHandoff() {
	const routingStore = useRoutingStore();

	onMounted(() => {
		if (didUpdateServerDate.value) return;

		const unwatch = watch(
			() => routingStore.activeRoute?.path,
			(newPath, oldPath) => {
				if (oldPath != null && newPath !== oldPath) {
					didUpdateServerDate.value = true;
					unwatch();
				}
			}
		);
	});
}

/**
 * Composable to persist today's date across SSR and client side.
 *
 * - During SSR, it sets the date using the server's timezone and stores it in `useState` (Nuxt payload).
 * - On the client, it updates to the correct date based on the timezone.
 * - Prevents hydration mismatches by using the precomputed SSR date before mounting.
 *
 * @returns Today's date shifted by the timezone.
 */
export function usePersistedTodaysDate() {
	const languageStore = useLanguageStore();

	// useState is serialized into the Nuxt payload, so the client reuses the server's value.
	const ssrTodaysDate = useState<Date | null>('ssr-todays-date', () => null);

	// On first SSR render, locale/timezone may not be loaded yet (e.g. on sports routes where
	// locales are fetched in a route guard). Fall back to UTC to avoid "Invalid time value".
	// The first valid write wins, so later calls don't change what the client receives.
	const setServerDate = () => {
		const current = ssrTodaysDate.value;
		if (current && !Number.isNaN(new Date(current).getTime())) return;

		const tz = languageStore.localeObject?.timezone || 'UTC';
		ssrTodaysDate.value = shiftDateWithTimezone(tz);
	};

	// Set during setup (before queries built from `todayDate` read it) but only once the timezone is
	// known, otherwise the UTC fallback would be locked in. Otherwise wait for onServerPrefetch.
	if (import.meta.server && languageStore.localeObject?.timezone) setServerDate();
	onServerPrefetch(setServerDate);

	useSsrDateHandoff();

	const todayDate = computed(() => {
		// if the user did navigate to another page we return the clients date
		if (ssrTodaysDate.value && !didUpdateServerDate.value) return new Date(ssrTodaysDate.value);

		const tz = languageStore.localeObject?.timezone || 'UTC';
		return shiftDateWithTimezone(tz);
	});

	return { todayDate };
}

export function usePersistedNow() {
	// Initialised on the server only (during setup) and serialized into the payload for the client.
	const ssrNow = useState<Date | null>('ssr-now', () => (import.meta.server ? new Date() : null));

	useSsrDateHandoff();

	const now = computed(() => {
		if (ssrNow.value && !didUpdateServerDate.value) {
			return new Date(ssrNow.value);
		}

		return new Date();
	});

	return { now };
}
